import type {
  FormattedPriceSettings,
  HybridSearchPayload,
  LiveEvent,
  PurchaseInfo,
  TvChannel,
  TvShowEpisode,
  TvShowSeason,
} from '@setplex/tria-api'
import type { TFunction } from 'i18next'
import type {
  ContentItem,
  ContentItemWithPrice,
  EventWithPrice,
  HybridSearchPayloadWithPrice,
  TvShowEpisodeWithPrice,
  TvShowSeasonWithPrice,
  TvWithPrice,
} from '~/shared/interfaces'

import {
  EventStatus,
  MediaTypes,
  PriceSettingsPaymentMethod,
  PurchaseInfoContentPurchaseType as PurchaseInfoContentPurchaseTypeEnum,
} from '@setplex/tria-api'
import {
  ONE_HOUR_MS,
  ONE_MINUTE_MS,
  ONE_SECOND_MS,
} from '~/shared/constants/time'
import { exists, nay } from '~/shared/helpers'
import {
  formatDateDuration,
  formatDateTimeWithYearUnified,
  formatTimeRangeUnified,
  formatYearToLocaleString,
  getFormattedDate,
} from '~/shared/helpers/date'

const getLabelPrice = ({
  free,
  purchaseInfo,
}: Pick<ContentItem, 'free' | 'purchaseInfo'>): string => {
  if (!free && purchaseInfo) {
    const label = getLabelPriceForPurchasedItem({
      purchaseInfo: purchaseInfo as PurchaseInfo,
      short: true,
    })
    return label ? label : ''
  }
  return ''
}

export const isPriceLabelShown = (priceSettings?: FormattedPriceSettings[]) => {
  if (!priceSettings) return false

  const hasPerItem = priceSettings.some(
    ({ paymentMethod }) => paymentMethod === PriceSettingsPaymentMethod.PerItem
  )
  const hasPerSet = priceSettings.some(
    ({ paymentMethod }) => paymentMethod === PriceSettingsPaymentMethod.PerSet
  )

  //We should show the price label only if the item has paymentMethod === PER_SET and no paymentMethod === PER_ITEM
  return !(hasPerSet && !hasPerItem)
}

export const extendMediaItemByLabelPrice = (
  item: ContentItem,
  isBundle = false
): ContentItemWithPrice => {
  const { purchaseInfo, free, priceSettings } = item

  let labelPrice = ''

  if ((isBundle && isPriceLabelShown(priceSettings)) || nay(isBundle))
    labelPrice = getLabelPrice({ free, purchaseInfo })

  return {
    ...item,
    labelPrice,
    rented:
      purchaseInfo?.contentPurchaseType ===
      PurchaseInfoContentPurchaseTypeEnum.Rented,
  }
}

// WBS sends i18n keys, not copy — the bundle owns the wording. PIN-lock
// (AC-8) is enforced on the card (`!locked`), not here; geoblock does not
// hide the badge.
export const resolveNewLabel = (
  { labels }: { labels?: string[] },
  t: TFunction
): string | undefined => (labels?.[0] ? t(labels[0]) : undefined)

const formatSoonEventStartTime = (
  startTime: string,
  endTime?: string
): string => {
  if (!exists(endTime)) {
    return formatDateTimeWithYearUnified(startTime)
  }

  return formatTimeRangeUnified(startTime, endTime, {
    alwaysIncludeDate: true,
  })
}

export const extendLiveEventByLabelPrice = (
  item: LiveEvent
): EventWithPrice => {
  const withLabelPrice = extendMediaItemByLabelPrice(item) as EventWithPrice
  const formattedStartTime =
    exists(item.startTime) && item.status === EventStatus.SOON
      ? formatSoonEventStartTime(item.startTime, item.endTime)
      : undefined

  return { ...withLabelPrice, formattedStartTime }
}

export const extendMediaItemByLabelPriceForHybridSearch = (
  item: HybridSearchPayload
): HybridSearchPayloadWithPrice => {
  if (item.contentType === MediaTypes.LIVE_EVENT) {
    return extendLiveEventByLabelPrice(item as LiveEvent)
  }

  return extendMediaItemByLabelPrice(item) as HybridSearchPayloadWithPrice
}

export const extendMediaItemByLabelPriceTvChannel = (
  item: TvChannel,
  isBundle = false
): TvWithPrice => {
  const { purchaseInfo, free, priceSettings } = item

  let labelPrice = ''

  if ((isBundle && isPriceLabelShown(priceSettings)) || nay(isBundle))
    labelPrice = getLabelPrice({ free, purchaseInfo })

  return {
    ...item,
    labelPrice,
    rented:
      purchaseInfo?.contentPurchaseType ===
      PurchaseInfoContentPurchaseTypeEnum.Rented,
  }
}

export const extendMediaItemByLabelPriceTvShowSeason = (
  item: TvShowSeason
): TvShowSeasonWithPrice => {
  const { purchaseInfo, free, year } = item
  const labelPrice = getLabelPrice({
    free,
    purchaseInfo,
  })

  return {
    ...item,
    labelPrice,
    rented:
      purchaseInfo?.contentPurchaseType ===
      PurchaseInfoContentPurchaseTypeEnum.Rented,
    ...(year && { year: formatYearToLocaleString(Number(year)) }),
  }
}

export const extendMediaItemByLabelPriceTvShowEpisode = (
  item: TvShowEpisode
): TvShowEpisodeWithPrice => {
  const { purchaseInfo, free } = item

  const labelPrice = getLabelPrice({
    free,
    purchaseInfo,
  })

  return {
    ...item,
    rented:
      purchaseInfo?.contentPurchaseType ===
      PurchaseInfoContentPurchaseTypeEnum.Rented,
    labelPrice: labelPrice,
  }
}

export const getLabelPriceForPurchasedItem = ({
  purchaseInfo,
  short,
}: {
  purchaseInfo: PurchaseInfo
  short?: boolean
}): string | undefined => {
  if (!purchaseInfo) return

  switch (purchaseInfo.contentPurchaseType) {
    case PurchaseInfoContentPurchaseTypeEnum.Rented: {
      const rentedUntilInMillis = purchaseInfo.rentedUntil
        ? new Date(purchaseInfo.rentedUntil).getTime()
        : Date.now()
      return getFormattedDate(rentedUntilInMillis, short)
    }
    default:
      return
  }
}

/**
 * Return the duration of content viewed as a percentage
 * @param stoppedTime - stop time while browsing
 * @param duration - content duration
 * @example duration = 10, stoppedTime = 2 -> 20%
 */
export const getProgress = ({
  duration,
  stoppedTime,
}: {
  duration?: number | null
  stoppedTime?: number | null
}): number | null => {
  if (!duration || !stoppedTime) return null

  return (stoppedTime * 100) / duration
}

const getTime = (timeLeftMs: number) => {
  const hours = Math.floor(timeLeftMs / ONE_HOUR_MS)
  const minutes = Math.floor((timeLeftMs - hours * ONE_HOUR_MS) / ONE_MINUTE_MS)
  const seconds = Math.floor(
    (timeLeftMs - minutes * ONE_MINUTE_MS) / ONE_SECOND_MS
  )

  return { hours, minutes, seconds }
}

export const getLeftTimeString = ({
  duration,
  stoppedTime,
  t,
}: {
  duration?: number | null
  stoppedTime?: number
  t: TFunction
}): string => {
  if (!duration || !stoppedTime) return ''

  const timeLeftMs = duration - stoppedTime
  const { hours, minutes } = getTime(timeLeftMs)
  const time = formatDateDuration({
    duration: { hours, minutes },
    short: true,
  })

  return t('time_left', {
    time,
  })
}

export const getViewedTimeString = (stoppedTime?: number): string => {
  if (!stoppedTime) return ''

  const { hours, minutes } = getTime(stoppedTime)
  const time = formatDateDuration({
    duration: { hours, minutes },
    short: true,
  })

  return time
}
